<template>
    <ChartPortlet ref="chartPortlet" color="success" :title="i18n('views.Index.NewSignups')" link="/user/">
        <template #description>
            <div class="kt-widget20__content kt-portlet__space-x">
                <span class="kt-widget20__number kt-font-success" v-if="isLoading">
                    &nbsp;<span class="kt-spinner kt-spinner--md kt-spinner--success"></span>
                </span>
                <span class="kt-widget20__number kt-font-success" v-if="!isLoading">
                    {{ totalAmount }}
                </span>
                <span class="kt-widget20__desc">{{ i18n("views.Index.NewUsers") }}</span>
            </div>
        </template>
    </ChartPortlet>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { DashboardDao } from "@/data/dao/service/DashboardDao";
    import { DateTime } from "@/data/util/DateTime";
    import { i18n } from "@/portal/Trans";
    import { CHART_COLORS, getGradient, invisibleColor } from "@/portal/components/dashboard/Utils";
    import { logError } from "@/shared/logging/errors";
    import { Chart } from "chart.js/auto";
    import { color } from "chart.js/helpers";
    import { computed, onMounted, ref } from "vue";

    import ChartPortlet from "@/portal/components/dashboard/ChartPortlet.vue";

    const dao = Factory.getInstance<DashboardDao>(DashboardDao);

    let chart: Chart;
    const chartPortlet = ref<InstanceType<typeof ChartPortlet> | null>(null);
    const canvas = computed(() => chartPortlet.value?.canvasElement);
    const isLoading = ref(true);
    const totalAmount = ref(0);

    onMounted(() => {
        chart = initChart();
        loadData();
    });

    function initChart(): Chart {
        const ctx = canvas.value!.getContext("2d")!;

        return new Chart(ctx, {
            type: "line",
            data: {
                labels: [],
                datasets: [
                    {
                        fill: "origin",
                        label: i18n("dashboard.signups"),
                        backgroundColor: getGradient(ctx, CHART_COLORS.lightGreen),
                        borderColor: color(CHART_COLORS.darkGreen).alpha(0.4).rgbString(),
                        pointBackgroundColor: invisibleColor,
                        pointBorderColor: invisibleColor,
                        pointHoverBackgroundColor: "#5867dd",
                        pointHoverBorderColor: color("#000000").alpha(0.1).rgbString(),
                        data: [],
                    },
                ],
            },
            options: {
                scales: {
                    x: {
                        display: false,
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: i18n("general.month"),
                        },
                    },
                    y: {
                        display: false,
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: i18n("dashboard.signups"),
                        },
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                elements: {
                    point: {
                        radius: 4,
                        borderWidth: 12,
                    },
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 10,
                        bottom: 0,
                    },
                    autoPadding: false,
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        mode: "nearest",
                        intersect: false,
                        padding: 10,
                        caretPadding: 10,
                        callbacks: {
                            label: (context) => {
                                const value = context.dataset.data[context.dataIndex];
                                return value + " neue Nutzer";
                            },
                        },
                    },
                },
            },
        });
    }

    async function loadData() {
        try {
            isLoading.value = true;
            totalAmount.value = 0;

            const ticks = await dao.getMonthlySignups();
            chart.data.labels = [];
            chart.data.datasets![0].data = [];

            for (const tick of ticks) {
                chart.data.labels.push(
                    `${DateTime.toDateString(tick.dateRange.to)} ${DateTime.toTimeString(tick.dateRange.to)}`
                );
                chart.data.datasets![0].data.push(tick.amount);
                totalAmount.value += tick.amount;
            }

            chart.update();
        } catch (e) {
            logError(e);
        } finally {
            isLoading.value = false;
        }
    }
</script>
